<template>
  <div :class="[$style.carouselContainer, spacingStateClass]">
    <div :class="$style.carouselBanner">
      <div :class="[$style.mediaItemContainer, spacingStateClass]">
        <template v-for="(item, idx) in mediaBanner.carouselItems">
          <component
            :is="retrieveComponent(item.itemType)"
            v-if="preloadItem(idx)"
            :key="idx"
            :media-item="item"
            :spacing-state="props.spacingState"
            :class="[$style.mediaItem, { [$style.visible]: currentIndex === idx }]"
            :is-header-banner="isHeaderBanner"
            :aspect-ratio="aspectRatio"
            :is-active="currentIndex === idx"
          ></component>
        </template>
      </div>
      <div ref="interactionOverlay" :class="[$style.navigationOverlay, spacingStateClass]">
        <div
          :class="[$style.leftThird, { [$style.half]: currentItemIsNotClickable }]"
          aria-label="Previous Item"
          role="button"
          tabindex="0"
          @click="prevItem"
          @keyup.enter="prevItem"
        ></div>
        <div
          :class="[$style.rightThird, { [$style.half]: currentItemIsNotClickable }]"
          aria-label="Next Item"
          role="button"
          tabindex="0"
          @click="nextItem"
          @keyup.enter="nextItem"
        ></div>
      </div>
    </div>
    <div :class="$style.carouselCaptionContainer">
      <div :class="$style.caption">
        <!-- populated by ImageBanner or VideoBanner -->
      </div>
      <div :class="$style.indicatorDots">
        <span
          v-for="dot in visibleDots"
          :key="dot.index"
          :class="[
            $style.dot,
            dot.isActive ? $style.activeDot : $style.inactiveDot,
            dot.size === 'small' ? $style.smallDot : $style.largeDot,
          ]"
          role="button"
          tabindex="0"
          :aria-label="'Go to slide ' + (dot.index + 1)"
          @click="goToSlide(dot.index)"
          @keyup.enter="goToSlide(dot.index)"
        ></span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Carousel } from '@/types/mediaBanner.interface';
import { computed, defineProps, onMounted, onUnmounted, ref, useCssModule } from 'vue';
import ImageBanner from '@/components/mediaBanner/ImageBanner.vue';
import {
  useAnalytics,
  useHandleError,
  useScreenSize,
  useSpacingState,
} from '@/composables/Common.js';
import { useSwipe } from '@vueuse/core';
import type { UseSwipeDirection } from '@vueuse/core';
import VideoBanner from '@/components/mediaBanner/VideoBanner.vue';

const $style = useCssModule();

const { ga4Event } = useAnalytics();
const { handleError } = useHandleError();
const { isMobile } = useScreenSize();

const props = withDefaults(
  defineProps<{
    mediaBanner: Carousel;
    spacingState?: string;
    isHeaderBanner?: boolean;
  }>(),
  {
    spacingState: 'Full Screen',
    isHeaderBanner: false,
  },
);

const currentIndex = ref(0);
const interactionOverlay = ref(null);
const screenWidth = ref(window.innerWidth);

let isTrackpadSwiping = false;
let swipeTimeout = null;

onMounted(() => {
  window.addEventListener('resize', updateScreenWidth);
  window.addEventListener('wheel', handleWheelEvent, { passive: false });
});

onUnmounted(() => {
  window.removeEventListener('resize', updateScreenWidth);
  window.removeEventListener('wheel', handleWheelEvent);
});

const { isSwiping, direction } = useSwipe(interactionOverlay, {
  onSwipeEnd(e: TouchEvent, direction: UseSwipeDirection) {
    switch (direction) {
      case 'left': {
        nextItem();
        break;
      }
      case 'right': {
        prevItem();
        break;
      }
    }
  },
});

const aspectRatio = computed(() => {
  return props.mediaBanner.aspectRatio ?? '16/9';
});
const currentItem = computed(() => {
  carouselItemsExist();

  return props.mediaBanner.carouselItems[currentIndex.value];
});
const currentItemHasNoActiveLink = computed(() => {
  carouselItemsExist();

  const item = props.mediaBanner.carouselItems[currentIndex.value];
  const hasLink = item.muxLongVideoId != null && item.muxLongVideoId != '';

  return !hasLink;
});
const currentItemIsNotClickable = computed(() => {
  return currentItemHasNoActiveLink.value || isMobile.value;
});
const visibleDots = computed(() => {
  const current = currentIndex.value;
  const dots = [];
  const totalSlides = props.mediaBanner.carouselItems.length;
  const isFewSlides = totalSlides <= 4;

  for (let i = 0; i < totalSlides; i++) {
    let size = 'large';

    if (!isFewSlides) {
      if ((i === 0 || i === totalSlides - 1) && i !== current) {
        size = 'small';
      }
    }

    dots.push({
      index: i,
      isActive: i === current,
      size: size,
    });
  }

  return dots;
});

const spacingStateClass = useSpacingState(props, $style);

function carouselItemsExist(): boolean {
  if (
    !props.mediaBanner ||
    !props.mediaBanner.carouselItems ||
    !Array.isArray(props.mediaBanner.carouselItems)
  ) {
    handleError(
      'CarouselBanner',
      `"carouselItems" not defined or not an array: ${props.mediaBanner}`,
      false,
    );

    return false;
  }

  return true;
}

function goToSlide(index: number): void {
  currentIndex.value = index;
  ga4Event({
    click_type: 'carousel_dot',
    component: 'CarouselBanner',
    content_group: 'media',
    link_text: `Moved to slide ${currentIndex.value + 1}`,
  });
}

function handleWheelEvent(e: WheelEvent): void {
  const threshold = 6;

  if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
    if (Math.abs(e.deltaX) > threshold) {
      e.preventDefault();

      if (isTrackpadSwiping) {
        return;
      }

      isTrackpadSwiping = true;

      if (e.deltaX > 0) {
        prevItem();
      }

      if (e.deltaX < 0) {
        nextItem();
      }

      clearTimeout(swipeTimeout);
      swipeTimeout = setTimeout(() => {
        isTrackpadSwiping = false;
      }, 300);
    }
  }
}

function nextItem(): void {
  currentIndex.value = (currentIndex.value + 1) % props.mediaBanner.carouselItems.length;
  ga4Event({
    click_type: 'carousel_next',
    component: 'CarouselBanner',
    content_group: 'media',
    link_text: `Moved to slide ${currentIndex.value + 1}`,
  });
}

function preloadItem(idx: number): boolean {
  const total = props.mediaBanner.carouselItems.length;
  const prevIndex = (currentIndex.value - 1 + total) % total;
  const nextIndex = (currentIndex.value + 1) % total;

  return idx == prevIndex || idx == currentIndex.value || idx == nextIndex;
}

function prevItem(): void {
  currentIndex.value =
    (currentIndex.value - 1 + props.mediaBanner.carouselItems.length) %
    props.mediaBanner.carouselItems.length;
  ga4Event({
    click_type: 'carousel_prev',
    component: 'CarouselBanner',
    content_group: 'media',
    link_text: `Moved to slide ${currentIndex.value + 1}`,
  });
}

function retrieveComponent(name: string): object | null {
  if (name === 'video') {
    return VideoBanner;
  }

  if (name === 'image') {
    return ImageBanner;
  }

  handleError('CarouselBanner', `Unsupported itemType: ${currentItem.value.itemType}`, false);

  return null;
}

function updateScreenWidth(): void {
  screenWidth.value = window.innerWidth;
}
</script>

<style lang="scss" module>
.carouselContainer {
  display: block;

  .carouselBanner {
    background-color: white;
    position: relative;
    display: block;
    width: 100%;

    .mediaItemContainer {
      position: relative;
      display: grid;
      grid-template-rows: 1fr;
      grid-template-columns: 1fr;
      width: 100%;
      place-items: center;

      .mediaItem {
        width: 100%;
        height: 100%;
        object-fit: cover;
        position: relative;
        grid-row: 1 / 1;
        grid-column: 1 / 1;
        transition-property: opacity;
        transition-timing-function: ease-in;
        transition-duration: 0.25s;
        opacity: 0;

        &.visible {
          opacity: 1;
        }
      }
    }

    .navigationOverlay {
      position: absolute;
      height: 100%;
      width: 100%;
      pointer-events: none;
      top: 0;
      left: 0;
      z-index: 10;
      -webkit-tap-highlight-color: rgba(0, 0, 0, 0);

      &.fullScreen {
        @include full-bleed-styles;

        & {
          position: absolute;
        }
      }

      .half {
        width: 50%;
      }

      .leftThird,
      .rightThird {
        cursor: pointer;
        height: 100%;
        pointer-events: auto;
        position: absolute;
        top: 0;
        width: 33.3333%;
      }

      .leftThird {
        left: 0;
      }

      .rightThird {
        right: 0;
      }

      * {
        outline: none;
        -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
      }
    }
  }

  .carouselCaptionContainer {
    align-items: center;
    box-sizing: border-box;
    display: flex;
    height: px-to-rem(35);
    justify-content: space-between;
    margin: 0;
    padding: 0.5rem 0;
    width: 100%;
    z-index: 20;

    .indicatorDots {
      align-items: center;
      display: flex;
      //gap: 0.25rem;
      gap: 0.188rem;
      margin: 0;

      .dot {
        border-radius: 50%;
        cursor: pointer;
        transition: all 0.3s ease;

        &.activeDot {
          background-color: #4a4a4a;
        }

        &.inactiveDot {
          background-color: #bfbfbf;
        }

        &.largeDot {
          height: 0.5rem;
          width: 0.5rem;
        }

        &.smallDot {
          height: 0.375rem;
          width: 0.375rem;
        }

        &:hover {
          transform: scale(1.1);
        }
      }
    }
  }
}

@media (min-width: $break-point) {
  .carouselContainer {
    &.column {
      display: inline-block;

      .carouselBanner {
        display: inline-block;
        width: auto;
      }
    }
  }
}
</style>
